/**
 * CampaignKeyArtDeliveryLog — operator-facing list of past key-art
 * shares for a campaign. Shows who got what, when they downloaded,
 * link status (active / expired / revoked), and lets the operator
 * copy the link or revoke it. v10.19.40.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, IconButton, Tooltip, Collapse, Button,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Block as RevokeIcon,
  ExpandMore as ExpandIcon,
  ChevronRight as CollapseIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { CANONICAL_ORIGIN } from '../config';


function fmtSize(n) {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}


function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}


function deviceFromUA(ua) {
  if (!ua) return null;
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Linux/.test(ua)) return 'Linux';
  return null;
}


function statusColor(status) {
  if (status === 'active') return 'success';
  if (status === 'expired') return 'default';
  if (status === 'revoked') return 'error';
  return 'default';
}


export default function CampaignKeyArtDeliveryLog({ campaignId, refreshKey }) {
  const [rows, setRows] = useState(null);
  const [openIds, setOpenIds] = useState(new Set());
  const [copiedToken, setCopiedToken] = useState(null);

  const load = () => {
    api.get(`/api/campaigns/${campaignId}/key-art/shares`)
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [campaignId, refreshKey]);

  const copyLink = async (token) => {
    const url = `${CANONICAL_ORIGIN}/d/k/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 1800);
    } catch (_e) { /* */ }
  };

  const revoke = async (share) => {
    if (!window.confirm(`Revoke the link for ${share.recipient_email || share.filename}? It'll stop working immediately. The audit trail stays.`)) return;
    try {
      await api.post(`/api/campaigns/${campaignId}/key-art/shares/${share.id}/revoke`);
      load();
    } catch (_e) { /* */ }
  };

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (rows === null) return null;
  if (rows.length === 0) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="caption" sx={{
          display: 'block', mb: 1, color: 'text.secondary',
          textTransform: 'uppercase', letterSpacing: 0.4,
          fontSize: '0.7rem', fontWeight: 600,
        }}>
          Delivery history
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          No shares yet. Pick tiles + click "Share file" / "Bundle + share" above to send a link.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="caption" sx={{
        display: 'block', mb: 1, color: 'text.secondary',
        textTransform: 'uppercase', letterSpacing: 0.4,
        fontSize: '0.7rem', fontWeight: 600,
      }}>
        Delivery history ({rows.length})
      </Typography>
      <Paper variant="outlined">
        <Table size="small" sx={{
          '& td, & th': { borderBottom: '1px solid', borderColor: 'divider', py: 0.75, px: 1 },
          '& th': { fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 },
        }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 30 }} />
              <TableCell>Sent</TableCell>
              <TableCell>Recipient</TableCell>
              <TableCell>What</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Downloads</TableCell>
              <TableCell align="right" sx={{ width: 110 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((s) => {
              const open = openIds.has(s.id);
              return (
                <React.Fragment key={s.id}>
                  <TableRow hover>
                    <TableCell sx={{ p: 0 }}>
                      <IconButton size="small" onClick={() => toggle(s.id)}>
                        {open ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell><Typography variant="caption">{fmtDateTime(s.created_at)}</Typography></TableCell>
                    <TableCell>
                      {s.recipient_email ? (
                        <Box>
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 500 }}>
                            {s.recipient_name || s.recipient_email}
                          </Typography>
                          {s.recipient_name && (
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontSize: '0.65rem' }}>
                              {s.recipient_email}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                          link only — no email
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        {s.item_count > 1 ? `${s.item_count} files (zip)` : s.filename}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                        {fmtSize(s.size_bytes)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={s.status}
                        color={statusColor(s.status)}
                        variant={s.status === 'active' ? 'filled' : 'outlined'}
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                      {s.status === 'active' && s.expires_at && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontSize: '0.62rem', mt: 0.25 }}>
                          expires {fmtDateTime(s.expires_at)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: s.download_count > 0 ? 600 : 400, color: s.download_count > 0 ? 'success.main' : 'text.disabled' }}
                      >
                        {s.download_count > 0
                          ? `${s.download_count} · last ${fmtDateTime(s.last_downloaded_at)}`
                          : 'not yet'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={copiedToken === s.token ? 'Copied!' : 'Copy share link'}>
                        <span>
                          <IconButton size="small" onClick={() => copyLink(s.token)} disabled={s.status !== 'active'}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Revoke link">
                        <span>
                          <IconButton size="small" onClick={() => revoke(s)} disabled={s.status !== 'active'} color="error">
                            <RevokeIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0, borderBottom: 'none' }}>
                      <Collapse in={open} unmountOnExit>
                        <Box sx={{ p: 1.5, bgcolor: 'background.default' }}>
                          {s.message && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.62rem', fontWeight: 600 }}>
                                Message
                              </Typography>
                              <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                                {s.message}
                              </Typography>
                            </Box>
                          )}
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.62rem', fontWeight: 600, mb: 0.5 }}>
                            Download events
                          </Typography>
                          {s.recent_events.length === 0 ? (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                              No downloads recorded.
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              {s.recent_events.map((ev, i) => (
                                <Typography key={i} variant="caption" sx={{ fontSize: '0.7rem' }}>
                                  <Box component="span" sx={{ color: 'text.secondary' }}>{fmtDateTime(ev.occurred_at)}</Box>
                                  {ev.ip_address && (
                                    <Box component="span" sx={{ color: 'text.disabled' }}> · {ev.ip_address}</Box>
                                  )}
                                  {deviceFromUA(ev.user_agent) && (
                                    <Box component="span" sx={{ color: 'text.disabled' }}> · {deviceFromUA(ev.user_agent)}</Box>
                                  )}
                                </Typography>
                              ))}
                            </Box>
                          )}
                          <Box sx={{ mt: 1 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => copyLink(s.token)}
                              disabled={s.status !== 'active'}
                            >
                              {copiedToken === s.token ? 'Copied!' : 'Copy share link'}
                            </Button>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
